import { Link, useLocation, NavLink } from "react-router-dom";
import { useState } from "react";
import { ShieldCheck, Menu, X, ChevronDown, Search, ExternalLink } from "lucide-react";
import MadeByWebsiteTech from "@/components/MadeByWebsiteTech";

/* ==========================================================================
   QST — Layout, Navbar, Footer, Section wrappers.
   Palette:
     navy   #0B2545  — primary
     orange #F97316  — safety-orange accent, primary CTA
     yellow #FACC15  — limited highlight
     bg     #F8FAFC  — light gray page bg
   ========================================================================== */

const NAV = [
  { label: "Solutions",         to: "/qst/solutions",       hasMenu: true,
    menu: [
      { to: "/qst/solutions/quality",       label: "Quality Control Training" },
      { to: "/qst/solutions/safety",        label: "Safety Training" },
      { to: "/qst/solutions/loss",          label: "Loss Prevention" },
      { to: "/qst/solutions/toolbox",       label: "Toolbox Talks" },
      { to: "/qst/solutions/rework",        label: "Rework Prevention" },
      { to: "/qst/solutions/crew",          label: "Crew Training" },
      { to: "/qst/solutions/multilingual",  label: "Multilingual Training" },
      { to: "/qst/solutions/documentation", label: "Training Documentation" },
    ],
  },
  { label: "Training Library",  to: "/qst/library" },
  { label: "Trades",            to: "/qst/trades" },
  { label: "Tools",             to: "/qst/tools",           hasMenu: true,
    menu: [
      { to: "/qst/tools/rework-cost",       label: "Rework Cost Calculator" },
      { to: "/qst/tools/injury-cost",       label: "Injury Cost Calculator" },
      { to: "/qst/tools/training-savings",  label: "Training Savings Calculator" },
      { to: "/qst/tools/toolbox-builder",   label: "Toolbox Talk Builder" },
      { to: "/qst/tools/checklist",         label: "Material Verification Checklist" },
    ],
  },
  { label: "Resources",         to: "/qst/resources" },
  { label: "For Organizations", to: "/qst/for-organizations" },
  { label: "Pricing",           to: "/qst/pricing" },
  { label: "About",             to: "/qst/about" },
];

export function QstNavbar() {
  const [open, setOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(null);
  return (
    <header className="sticky top-0 z-40 bg-white border-b border-slate-200"
            data-testid="qst-navbar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
        <Link to="/qst" className="flex items-center gap-2.5 font-black text-[#0B2545] tracking-tight" data-testid="qst-logo">
          <div className="w-9 h-9 rounded-md bg-gradient-to-br from-[#0B2545] to-[#1D4ED8] grid place-items-center text-white shadow-md">
            <ShieldCheck className="w-5 h-5" />
          </div>
          <div className="leading-tight">
            <div className="text-[15px]">QUALITY SAFETY</div>
            <div className="text-[9px] tracking-[0.25em] text-[#F97316] font-bold">TIMES</div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {NAV.map((n) => (
            <div key={n.label} className="relative"
                 onMouseEnter={() => n.hasMenu && setMenuOpen(n.label)}
                 onMouseLeave={() => setMenuOpen(null)}>
              <NavLink to={n.to}
                className={({ isActive }) =>
                  `inline-flex items-center gap-1 px-3 py-2 rounded-md text-sm font-semibold transition ${isActive ? "text-[#0B2545] bg-slate-100" : "text-slate-700 hover:text-[#0B2545] hover:bg-slate-100"}`
                }
                data-testid={`qst-nav-${n.label.toLowerCase().replace(/\s+/g,'-')}`}>
                {n.label}
                {n.hasMenu && <ChevronDown className="w-3.5 h-3.5" />}
              </NavLink>
              {n.hasMenu && menuOpen === n.label && (
                <div className="absolute top-full left-0 mt-1 w-64 bg-white border border-slate-200 rounded-lg shadow-2xl py-1.5 z-50">
                  {n.menu.map((m) => (
                    <Link key={m.to} to={m.to}
                      className="block px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 hover:text-[#0B2545]">
                      {m.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <button className="p-2 hover:bg-slate-100 rounded-md" aria-label="Search" data-testid="qst-search-btn">
            <Search className="w-4 h-4 text-slate-600" />
          </button>
          <Link to="/qst/signin" className="text-sm font-semibold text-slate-700 hover:text-[#0B2545]" data-testid="qst-signin">
            Sign In
          </Link>
          <Link to="/qst/signup"
                className="inline-flex items-center gap-1.5 rounded-md bg-[#F97316] hover:bg-[#EA580C] text-white px-4 py-2 text-sm font-bold shadow-sm transition"
                data-testid="qst-start-free">
            Start Free
          </Link>
        </div>

        <button className="lg:hidden p-2" onClick={() => setOpen(!open)} aria-label="Menu"
                data-testid="qst-mobile-toggle">
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden border-t border-slate-200 bg-white" data-testid="qst-mobile-menu">
          {NAV.map((n) => (
            <Link key={n.label} to={n.to} onClick={() => setOpen(false)}
                  className="block px-6 py-3 border-b border-slate-100 text-slate-800 font-semibold">
              {n.label}
            </Link>
          ))}
          <div className="p-6 flex gap-3">
            <Link to="/qst/signin" onClick={() => setOpen(false)}
                  className="flex-1 rounded-md border border-slate-300 py-2.5 text-center font-semibold">
              Sign In
            </Link>
            <Link to="/qst/signup" onClick={() => setOpen(false)}
                  className="flex-1 rounded-md bg-[#F97316] text-white py-2.5 text-center font-bold">
              Start Free
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}

export function QstFooter() {
  return (
    <footer className="bg-[#0B2545] text-slate-200 mt-16" data-testid="qst-footer">
      <div className="max-w-7xl mx-auto px-6 py-14 grid grid-cols-2 md:grid-cols-4 gap-8">
        <div className="col-span-2 md:col-span-1">
          <div className="flex items-center gap-2.5 font-black text-white">
            <div className="w-9 h-9 rounded-md bg-[#F97316] grid place-items-center">
              <ShieldCheck className="w-5 h-5 text-white" />
            </div>
            <div className="leading-tight">
              <div className="text-[15px]">QUALITY SAFETY</div>
              <div className="text-[9px] tracking-[0.25em] text-[#F97316] font-bold">TIMES</div>
            </div>
          </div>
          <p className="mt-4 text-sm text-slate-300 leading-relaxed">
            Expert-authored quality, safety and loss-prevention training built for construction trades.
          </p>
          <p className="mt-4 text-xs text-slate-400">
            Reduce Rework · Prevent Injuries · Build Better
          </p>
        </div>
        <div>
          <div className="text-xs font-bold uppercase tracking-widest text-white mb-3">Product</div>
          <ul className="space-y-2 text-sm">
            <li><Link to="/qst/library" className="hover:text-white">Training Library</Link></li>
            <li><Link to="/qst/trades" className="hover:text-white">Trades</Link></li>
            <li><Link to="/qst/tools" className="hover:text-white">Tools</Link></li>
            <li><Link to="/qst/pricing" className="hover:text-white">Pricing</Link></li>
          </ul>
        </div>
        <div>
          <div className="text-xs font-bold uppercase tracking-widest text-white mb-3">Solutions</div>
          <ul className="space-y-2 text-sm">
            <li><Link to="/qst/for-organizations" className="hover:text-white">For Organizations</Link></li>
            <li><Link to="/qst/solutions/toolbox" className="hover:text-white">Toolbox Talks</Link></li>
            <li><Link to="/qst/solutions/multilingual" className="hover:text-white">Multilingual</Link></li>
            <li><Link to="/qst/story/tell" className="hover:text-white">Tell Your Story</Link></li>
          </ul>
        </div>
        <div>
          <div className="text-xs font-bold uppercase tracking-widest text-white mb-3">Company</div>
          <ul className="space-y-2 text-sm">
            <li><Link to="/qst/about" className="hover:text-white">About</Link></li>
            <li><Link to="/qst/contact" className="hover:text-white">Contact</Link></li>
            <li><Link to="/qst/legal/terms" className="hover:text-white">Terms</Link></li>
            <li><Link to="/qst/legal/privacy" className="hover:text-white">Privacy</Link></li>
            <li><Link to="/qst/legal/accessibility" className="hover:text-white">Accessibility</Link></li>
          </ul>
        </div>
      </div>
      <div className="border-t border-slate-700/60">
        <div className="max-w-7xl mx-auto px-6 py-5 flex flex-wrap items-center justify-between gap-3 text-xs text-slate-400">
          <div>© {new Date().getFullYear()} Quality Safety Times. All rights reserved.</div>
          <div className="flex items-center gap-4">
            <span>Content authored by construction professionals</span>
            <a href="https://www.linkedin.com/company/quality-safety-times/" target="_blank" rel="noreferrer"
               className="inline-flex items-center gap-1 hover:text-white">
              LinkedIn <ExternalLink className="w-3 h-3" />
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}

export default function QstLayout({ children }) {
  return (
    <div className="min-h-screen flex flex-col bg-white text-slate-900 antialiased">
      <QstNavbar />
      <main className="flex-1">
        {children}
      </main>
      <QstFooter />
      <MadeByWebsiteTech variant="dark" />
    </div>
  );
}

/* Reusable AI-safety disclaimer strip — required by the QST brand promise. */
export function AiSafetyBadge({ children }) {
  return (
    <div className="inline-flex items-start gap-2 rounded-md bg-slate-50 border border-slate-200 px-3 py-2 text-xs text-slate-700 max-w-2xl"
         data-testid="qst-ai-safety-badge">
      <ShieldCheck className="w-4 h-4 text-[#059669] mt-0.5 flex-shrink-0" />
      <div>
        <strong className="text-slate-900">AI at QST</strong> only summarizes expert-approved content
        and never invents safety requirements. {children}
      </div>
    </div>
  );
}
